import { Button, Tabs } from "@heroui/react";
import { useReducedMotion } from "framer-motion";
import { memo, useEffect, useRef, useState, type ReactNode } from "react";
import { SummaryChips } from "@funnel-ui/primitives/SummaryChips";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

type RevealCharacter = {
  id: string;
  name: string;
  imageUrl: string;
  bio?: string;
};

export type RevealSummaryTab = {
  id: string;
  label: string;
  content: ReactNode;
};

type CharacterRevealGateProps = {
  characters: RevealCharacter[];
  /** Optional gate countdown ms before reveal triggers automatically. */
  gateMs?: number;
  /** Duration in ms for the progressive unblur animation. Defaults to 1500. */
  revealDurationMs?: number;
  /** Starting blur radius in px. Defaults to 20. */
  blurStart?: number;
  /** Headline shown before reveal. */
  preRevealHeadline?: string;
  /** Headline shown after reveal. */
  postRevealHeadline?: string;
  ctaLabel?: string;
  onCta?: () => void;
  /**
   * Optional summary chips rendered between the character grid and the CTA
   * (post-reveal only). Useful for echoing personality / scenario / style in
   * single-character mode.
   */
  chips?: string[];
  /**
   * Optional tabbed summary rendered between the character grid and the CTA
   * (post-reveal only). When present, takes precedence over `chips` so the
   * caller can render chips inside one of the tab panels.
   */
  summaryTabs?: RevealSummaryTab[];
  className?: string;
  testId?: string;
};

const isSingleCharacterMode = (characters: RevealCharacter[]): boolean => characters.length === 1;

// ---------------------------------------------------------------------------
// Progressive reveal hook — drives blur from `blurStart` → 0 over
// `durationMs` using rAF for smooth 60fps animation with easeOut curve.
// ---------------------------------------------------------------------------
const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

const useProgressiveReveal = (
  revealed: boolean,
  durationMs: number,
  blurStart: number,
  prefersReduced: boolean,
): { blur: number; scale: number; done: boolean } => {
  const alreadyRevealed = useRef(revealed);
  const [progress, setProgress] = useState(revealed ? 1 : 0);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!revealed || prefersReduced) {
      if (revealed) setProgress(1);
      return;
    }
    if (alreadyRevealed.current) {
      setProgress(1);
      return;
    }
    alreadyRevealed.current = true;
    if (durationMs <= 0) {
      setProgress(1);
      return;
    }

    startTimeRef.current = null;

    const step = (timestamp: number): void => {
      if (startTimeRef.current === null) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const raw = Math.min(elapsed / durationMs, 1);
      const eased = easeOutCubic(raw);
      setProgress(eased);
      if (raw < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [revealed, durationMs, prefersReduced]);

  const blur = blurStart * (1 - progress);
  const scale = 1 + 0.1 * (1 - progress);
  const done = progress >= 1;
  return { blur, scale, done };
};

const ChipRow = ({ chips, prefersReduced }: { chips: string[]; prefersReduced: boolean }) => (
  <SummaryChips
    chips={chips}
    prefersReduced={prefersReduced}
    ariaLabel="Match summary"
    staggerMs={60}
  />
);

const RevealSummary = ({
  chips,
  summaryTabs,
  prefersReduced,
  testId,
}: {
  chips?: string[];
  summaryTabs?: RevealSummaryTab[];
  prefersReduced: boolean;
  testId?: string;
}): ReactNode => {
  if (summaryTabs && summaryTabs.length > 0) {
    return <SummaryTabs tabs={summaryTabs} testId={testId} />;
  }
  if (chips && chips.length > 0) {
    return <ChipRow chips={chips} prefersReduced={prefersReduced} />;
  }
  return null;
};

const SummaryTabs = ({ tabs, testId }: { tabs: RevealSummaryTab[]; testId?: string }) => (
  <Tabs className="w-full max-w-md" data-testid={testId ? `${testId}-tabs` : undefined}>
    <Tabs.List className="flex gap-2 border-b border-[hsl(var(--brand-bg-foreground)/0.1)]">
      {tabs.map((tab) => (
        <Tabs.Tab
          key={tab.id}
          id={tab.id}
          className="px-3 py-2 text-sm font-medium opacity-70 data-[selected=true]:opacity-100"
        >
          {tab.label}
        </Tabs.Tab>
      ))}
    </Tabs.List>
    {tabs.map((tab) => (
      <Tabs.Panel key={tab.id} id={tab.id} className="pt-3">
        {tab.content}
      </Tabs.Panel>
    ))}
  </Tabs>
);

// ---------------------------------------------------------------------------
// Single character portrait with progressive reveal
// ---------------------------------------------------------------------------
const CharacterPortrait = ({
  character,
  index,
  blur,
  scale,
  done,
  prefersReduced,
}: {
  character: RevealCharacter;
  index: number;
  blur: number;
  scale: number;
  done: boolean;
  prefersReduced: boolean;
}) => (
  <div
    className="flex flex-col items-center gap-2"
    style={{
      animation: prefersReduced
        ? undefined
        : `fc-fade-in-up 480ms ${index * 80}ms cubic-bezier(0.32,0.72,0,1) backwards`,
    }}
  >
    <div
      className="relative aspect-3/4 w-full overflow-hidden rounded-2xl"
      style={{ background: "hsl(var(--brand-bg-foreground) / 0.08)" }}
    >
      <ResponsiveAsset
        src={character.imageUrl}
        alt=""
        loading="eager"
        className="h-full w-full object-cover"
        style={{
          filter: `blur(${blur}px)`,
          transform: `scale(${scale})`,
          willChange: blur > 0 ? "filter, transform" : "auto",
        }}
      />
      {!done && (
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.4))",
          }}
        />
      )}
    </div>
    <span
      className="text-sm font-semibold"
      style={{
        color: "hsl(var(--brand-bg-foreground))",
        opacity: done ? 1 : 0,
        transition: prefersReduced ? "none" : `opacity 320ms ${done ? "200ms" : "0ms"} ease-out`,
      }}
    >
      {character.name}
    </span>
  </div>
);

const CharacterRevealGateImpl = ({
  characters,
  gateMs = 0,
  revealDurationMs = 1500,
  blurStart = 20,
  preRevealHeadline = "Almost there...",
  postRevealHeadline = "Meet your top matches",
  ctaLabel = "Continue",
  onCta,
  chips,
  summaryTabs,
  className,
  testId,
}: CharacterRevealGateProps) => {
  const prefersReduced = useReducedMotion();
  const [revealed, setRevealed] = useState(gateMs <= 0);
  const single = isSingleCharacterMode(characters);
  const { blur, scale, done } = useProgressiveReveal(
    revealed,
    revealDurationMs,
    blurStart,
    !!prefersReduced,
  );

  useEffect(() => {
    if (gateMs <= 0) return;
    const t = setTimeout(() => setRevealed(true), gateMs);
    return () => clearTimeout(t);
  }, [gateMs]);

  const characterGridClasses = single
    ? "grid w-full max-w-xs grid-cols-1 gap-3"
    : "grid w-full grid-cols-3 gap-3";
  const visibleCharacters = single ? characters : characters.slice(0, 3);

  return (
    <section data-testid={testId} className={`flex flex-col items-center gap-6 ${className ?? ""}`}>
      <h1
        className="text-center text-2xl font-semibold leading-tight"
        style={{ color: "hsl(var(--brand-bg-foreground))" }}
      >
        {revealed ? postRevealHeadline : preRevealHeadline}
      </h1>

      <div className={characterGridClasses}>
        {visibleCharacters.map((character, i) => (
          <CharacterPortrait
            key={character.id}
            character={character}
            index={i}
            blur={blur}
            scale={scale}
            done={done}
            prefersReduced={!!prefersReduced}
          />
        ))}
      </div>

      {done && (
        <RevealSummary
          chips={chips}
          summaryTabs={summaryTabs}
          prefersReduced={!!prefersReduced}
          testId={testId}
        />
      )}

      {done && (
        <Button variant="primary" size="lg" onPress={onCta} className="rounded-full">
          {ctaLabel}
        </Button>
      )}
    </section>
  );
};

export const CharacterRevealGate = memo(CharacterRevealGateImpl);

export default CharacterRevealGate;
